import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  MDF_FUND_STATUSES,
  MDF_FUND_STATUS_LABEL,
  type MarketingFundRow,
  type MdfFundStatus,
} from "@colab/shared";
import { formatApiError } from "@/lib/format-api-error";
import { useUpdateFund } from "./use-update-fund";
import { useCloseFund } from "./use-close-fund";

// Sub-bucket 15 — Configuration tab. Slug is read-only post-create (URL
// stability). Status / total / period / notes / name are editable here.
// Close-fund is a separate dedicated endpoint + dropdown with confirmation
// because it surfaces a clearer audit event than the generic PATCH.
//
// Form is single-submit (Save changes) rather than per-field autosave
// because the fund-level fields are reviewed together: changing the period
// + total + status in one save matches the admin's mental model.

export function FundConfigurationTab({
  fund,
  hasAllocations,
}: {
  fund: MarketingFundRow;
  hasAllocations: boolean;
}) {
  const update = useUpdateFund();
  const close = useCloseFund();

  const [name, setName] = useState(fund.name);
  const [totalEur, setTotalEur] = useState(String(fund.total_amount_eur));
  const [periodStart, setPeriodStart] = useState(fund.period_start);
  const [periodEnd, setPeriodEnd] = useState(fund.period_end);
  const [status, setStatus] = useState<MdfFundStatus>(fund.status);
  const [notes, setNotes] = useState(fund.notes ?? "");

  const disabled = update.isPending || close.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    const totalParsed = Number(totalEur);
    if (!Number.isInteger(totalParsed) || totalParsed < 0) {
      toast.error("Total must be a whole number of EUR");
      return;
    }
    if (new Date(periodEnd) < new Date(periodStart)) {
      toast.error("Period end must be on or after period start");
      return;
    }
    update.mutate(
      {
        id: fund.id,
        input: {
          name: name.trim(),
          total_amount_eur: totalParsed,
          period_start: periodStart,
          period_end: periodEnd,
          notes: notes.trim() ? notes.trim() : undefined,
          status,
        },
      },
      {
        onSuccess: () => toast.success("Fund updated"),
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to update fund")),
      },
    );
  }

  function onClose() {
    close.mutate(
      { id: fund.id },
      {
        onSuccess: () => toast.success("Fund closed"),
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to close fund")),
      },
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="grid gap-5 rounded-xl border bg-card p-5"
    >
      <div className="grid gap-1.5">
        <Label htmlFor="mdf-cfg-name">Name</Label>
        <Input
          id="mdf-cfg-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={disabled}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="mdf-cfg-slug">Slug</Label>
        <Input id="mdf-cfg-slug" value={fund.slug} disabled />
        <p className="text-[11px] text-muted-foreground">
          {hasAllocations
            ? "Slug is locked because allocations exist — preserves URL stability."
            : "Slug cannot be edited after creation."}
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="mdf-cfg-total">Total amount (EUR)</Label>
        <Input
          id="mdf-cfg-total"
          type="number"
          min={0}
          step={1}
          value={totalEur}
          onChange={(e) => setTotalEur(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="mdf-cfg-period-start">Period start</Label>
          <Input
            id="mdf-cfg-period-start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="mdf-cfg-period-end">Period end</Label>
          <Input
            id="mdf-cfg-period-end"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="mdf-cfg-status">Status</Label>
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as MdfFundStatus)}
          disabled={disabled}
        >
          <SelectTrigger id="mdf-cfg-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MDF_FUND_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {MDF_FUND_STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="mdf-cfg-notes">Notes</Label>
        <Textarea
          id="mdf-cfg-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={disabled}
        />
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {status !== "closed" ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                {close.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Close fund
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Close this fund?</AlertDialogTitle>
                <AlertDialogDescription>
                  Partners will no longer be able to submit new requests against
                  any allocation in this fund. In-flight pending requests stay
                  reviewable.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={disabled}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onClose} disabled={disabled}>
                  Close fund
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={disabled}>
          {update.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          <Save className="mr-1.5 h-3.5 w-3.5" />
          Save changes
        </Button>
      </div>
    </form>
  );
}
